import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Panel, Pill, Loading, ErrorNote, Empty, formatDateTime } from '../components/ui';
import { navigate, useRoute } from '../lib/router';
import { useRight } from '../lib/auth';
import { ITEM_TYPE_LABELS, type ItemType } from '@inbrief/shared';

interface Citation { id: string; blockIndex: number; pageNumber: number; citedText: string }
interface Item {
  id: string; type: ItemType; title: string; detail: string;
  clause_reference: string | null; applies_to: string | null;
  due_date: string | null; frequency: string | null;
  value: number | null; unit: string | null;
  confidence: 'high' | 'medium' | 'low'; status: string;
  edited_title: string | null; edited_detail: string | null;
  citation_mismatch: boolean; citations: Citation[];
}
interface Block {
  id: string; block_index: number; page_number: number;
  text: string; clause_ref: string | null;
}
interface DocumentRow { id: string; title: string; item_count: string }

const TYPE_TAG: Record<ItemType, string> = {
  obligation: 'tag-requirement', kpi: 'tag-kpi', deadline: 'tag-deadline',
  funding: 'tag-funding', eligibility: 'tag-eligibility',
  reporting: 'tag-change', definition: 'tag-referral',
};

const CONFIDENCE_TONE: Record<string, string> = { high: 'approved', medium: 'review', low: 'rejected' };
const STATUS_TONE: Record<string, string> = {
  pending: 'draft', accepted: 'approved', edited: 'approved', rejected: 'rejected',
};

interface QualityData {
  byType: { type: string; total: string; pending: string; accepted: string; edited: string; rejected: string }[];
  reviewed: number;
  corrected: number;
  editRate: number | null;
}

/**
 * The edit rate: what fraction of extracted items a human had to correct.
 *
 * This is the number the pilot turns on. Above about 20% the extraction is not
 * trusted enough to act on; below about 5% it is probably not extracting
 * enough to be worth having. It was being computed and never shown, which made
 * it useless — the judgement belongs to the person doing the reviewing.
 */
function ExtractionQuality() {
  const { data } = useQuery({
    queryKey: ['quality'],
    queryFn: () => api.get<QualityData>('/api/quality'),
  });

  if (!data || data.reviewed === 0) return null;

  const pct = data.editRate === null ? null : Math.round(data.editRate * 100);
  const verdict =
    pct === null ? null
    : pct > 20 ? { tone: 'rejected', text: 'Above 20% — not yet reliable enough to act on without reading the source.' }
    : pct < 5 ? { tone: 'review', text: 'Below 5% — check it is finding everything, not just being agreed with.' }
    : { tone: 'approved', text: 'Within the range where the output is worth trusting.' };

  return (
    <details className="quality quality-edit-rate">
      <summary>
        Extraction quality: {pct}% of {data.reviewed} reviewed item
        {data.reviewed === 1 ? '' : 's'} needed correcting
      </summary>
      <div className="quality-body">
        {verdict ? (
          <div className={verdict.tone === 'approved' ? 'note-box' : 'warn-box'}>{verdict.text}</div>
        ) : null}
        <div className="table-wrap">
          <table className="dt">
            <caption className="visually-hidden">Review outcomes by item type</caption>
            <thead>
              <tr>
                <th scope="col">Type</th><th scope="col">Total</th>
                <th scope="col">Accepted</th><th scope="col">Edited</th>
                <th scope="col">Rejected</th><th scope="col">To review</th>
              </tr>
            </thead>
            <tbody>
              {data.byType.map((r) => (
                <tr key={r.type}>
                  <th scope="row">{ITEM_TYPE_LABELS[r.type as ItemType] ?? r.type}</th>
                  <td>{r.total}</td><td>{r.accepted}</td>
                  <td>{r.edited}</td><td>{r.rejected}</td><td>{r.pending}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}

interface Correction {
  id: string; action: 'edited' | 'rejected'; created_at: string;
  type: string; clause_reference: string | null; confidence: string;
  document_title: string; document_type: string | null;
  previous_title: string; new_title: string; previous_detail: string; new_detail: string;
  note: string | null; reviewer_name: string;
}

interface RecurringCorrection {
  type: string; document_type: string | null;
  edited: string; rejected: string; documents: string;
}

/**
 * What people are actually correcting.
 *
 * The edit rate above says *that* the extraction is wrong. It cannot say how,
 * and no prompt has ever been improved by looking at a percentage. These are
 * the labelled errors — what the model said, what a person changed it to, and
 * the note explaining why — which is what a prompt change or a new gold-set
 * entry gets written from. A correction recurring across several documents is
 * a prompt problem; one recurring inside a single document usually means that
 * document is unusual.
 */
function Corrections() {
  const { data } = useQuery({
    queryKey: ['corrections'],
    queryFn: () => api.get<{ corrections: Correction[]; recurring: RecurringCorrection[] }>(
      '/api/corrections',
    ),
  });

  if (!data?.corrections.length) return null;

  return (
    <details className="quality quality-corrections">
      <summary>
        What reviewers are correcting ({data.corrections.length} change
        {data.corrections.length === 1 ? '' : 's'})
      </summary>
      <div className="quality-body">
        <p className="field-hint">
          Every edit and rejection is a labelled error. Read them before changing the
          extraction prompt, and add the ones that keep coming back to the gold set.
        </p>

        {data.recurring.length ? (
          <div className="table-wrap">
            <table className="dt">
              <caption className="visually-hidden">Corrections by item type and document type</caption>
              <thead>
                <tr>
                  <th scope="col">Item type</th><th scope="col">Document type</th>
                  <th scope="col">Edited</th><th scope="col">Rejected</th>
                  <th scope="col">Across documents</th>
                </tr>
              </thead>
              <tbody>
                {data.recurring.map((r) => (
                  <tr key={`${r.type}-${r.document_type}`}>
                    <th scope="row">{ITEM_TYPE_LABELS[r.type as ItemType] ?? r.type}</th>
                    <td>{r.document_type ?? '—'}</td>
                    <td>{r.edited}</td><td>{r.rejected}</td><td>{r.documents}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="timeline">
          {data.corrections.map((c) => (
            <div className="tl-item" key={c.id}>
              <div className="tl-when">{formatDateTime(c.created_at)}</div>
              <div className="tl-what">
                <Pill tone={c.action === 'rejected' ? 'rejected' : 'review'}>
                  {c.action === 'rejected' ? 'Rejected' : 'Edited'}
                </Pill>{' '}
                {ITEM_TYPE_LABELS[c.type as ItemType] ?? c.type}
                {c.clause_reference ? ` — ${c.clause_reference}` : ''} in {c.document_title}
                <div className="correction-was">Model: “{c.previous_title}”</div>
                {c.action === 'edited' && c.new_title !== c.previous_title ? (
                  <div className="correction-now">Corrected to: “{c.new_title}”</div>
                ) : null}
                {c.note ? <div className="correction-note">Note: {c.note}</div> : null}
              </div>
              <div className="tl-who">{c.reviewer_name}</div>
            </div>
          ))}
        </div>

        <a className="btn btn-ghost btn-small" href="/api/corrections/export">
          Export the corrections
        </a>
      </div>
    </details>
  );
}

interface RunSummary {
  id: string; prompt_version: string; model: string; created_at: string; itemCount: number;
}

interface ItemChange {
  kind: 'unchanged' | 'changed' | 'added' | 'removed';
  current?: { id: string; type: string; title: string; clauseReference: string | null };
  previous?: {
    id: string; type: string; title: string; clauseReference: string | null;
    status: string; editedTitle: string | null;
  };
  matchedOn?: string;
  differences: string[];
  previouslyRejected: boolean;
}

interface Comparison {
  current: RunSummary;
  previous: RunSummary;
  changes: ItemChange[];
  summary: {
    unchanged: number; changed: number; added: number; removed: number;
    reviewedAndLost: number; rejectedAndBack: number;
  };
}

/**
 * What a re-extraction changed.
 *
 * You will change the prompt, and when you do, someone has to be able to say
 * whether the new run is better or merely different. Without this a
 * re-extraction is a black box: the new items replace the old ones on screen
 * and every review decision made against the old run is silently stranded.
 *
 * Nothing here changes a review status. Carrying a human's "accepted" across
 * to fresh model output on the strength of a fuzzy match would mean an item
 * nobody read is marked as read.
 */
function RunComparison({ documentId, runId }: { documentId: string; runId: string }) {
  const { data, error } = useQuery({
    queryKey: ['compare', documentId, runId],
    queryFn: () => api.get<Comparison>(`/api/documents/${documentId}/runs/${runId}/compare`),
    retry: false,
  });

  // There is nothing to compare against until a document has been extracted
  // twice, which is the normal state.
  if (error || !data) return null;

  const notable = data.changes.filter((c) => c.kind !== 'unchanged');

  return (
    <details className="quality quality-runs">
      <summary>
        What the latest extraction changed ({data.summary.added} new, {data.summary.changed} changed,
        {' '}{data.summary.removed} no longer found)
      </summary>
      <div className="quality-body">
        <p className="field-hint">
          Prompt {data.previous.prompt_version} → {data.current.prompt_version}, run{' '}
          {formatDateTime(data.previous.created_at)} and {formatDateTime(data.current.created_at)}.
        </p>

        {data.summary.rejectedAndBack ? (
          <div className="warn-box">
            {data.summary.rejectedAndBack} item{data.summary.rejectedAndBack === 1 ? '' : 's'} a
            reviewer had rejected {data.summary.rejectedAndBack === 1 ? 'is' : 'are'} back in this
            run. Read {data.summary.rejectedAndBack === 1 ? 'it' : 'them'} again before accepting.
          </div>
        ) : null}
        {data.summary.reviewedAndLost ? (
          <div className="warn-box">
            {data.summary.reviewedAndLost} item{data.summary.reviewedAndLost === 1 ? '' : 's'} you
            had already reviewed {data.summary.reviewedAndLost === 1 ? 'is' : 'are'} not in this
            run. That review work would have to be done again.
          </div>
        ) : null}
        {!notable.length ? (
          <div className="note-box">Nothing changed between these two runs.</div>
        ) : null}

        <div className="timeline">
          {notable.map((c, i) => {
            const item = c.current ?? c.previous!;
            return (
              <div className="tl-item" key={`${c.kind}-${item.id}-${i}`}>
                <div className="tl-when">
                  <Pill tone={c.kind === 'removed' ? 'rejected' : c.kind === 'added' ? 'approved' : 'review'}>
                    {c.kind === 'added' ? 'New' : c.kind === 'removed' ? 'Gone' : 'Changed'}
                  </Pill>
                </div>
                <div className="tl-what">
                  {ITEM_TYPE_LABELS[item.type as ItemType] ?? item.type}
                  {item.clauseReference ? ` — ${item.clauseReference}` : ''}
                  <div className="correction-was">{item.title}</div>
                  {c.differences.length ? (
                    <div className="correction-note">Changed: {c.differences.join(', ')}</div>
                  ) : null}
                  {c.previouslyRejected ? (
                    <div className="correction-note">A reviewer had rejected this before.</div>
                  ) : null}
                </div>
                <div className="tl-who">
                  {c.kind === 'removed' && c.previous!.status !== 'pending'
                    ? `Was ${c.previous!.status}`
                    : c.matchedOn
                      ? `Matched on ${c.matchedOn}`
                      : ''}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </details>
  );
}

interface ReviewRecord {
  id: string; action: string; created_at: string;
  reviewer_name: string; reviewer_role: string;
  previous_title: string | null; new_title: string | null; note: string | null;
}

/** The review trail for one item — who changed what, and when. */
function ItemHistory({ itemId }: { itemId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['item-history', itemId],
    queryFn: () => api.get<{ history: ReviewRecord[] }>(`/api/items/${itemId}/history`),
  });

  if (isLoading) return <Loading what="Loading history" />;
  if (!data?.history.length) return <p className="field-hint">No review recorded yet.</p>;

  return (
    <div className="timeline">
      {data.history.map((h) => (
        <div className="tl-item" key={h.id}>
          <div className="tl-when">{formatDateTime(h.created_at)}</div>
          <div className="tl-what">
            {h.action}
            {h.action === 'edited' && h.previous_title !== h.new_title
              ? ` — "${h.previous_title}" became "${h.new_title}"`
              : ''}
          </div>
          <div className="tl-who">{h.reviewer_name} — {h.reviewer_role}</div>
        </div>
      ))}
    </div>
  );
}

export function Breakdown() {
  const route = useRoute();
  const canReview = useRight('extraction');
  const queryClient = useQueryClient();

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ItemType | 'all'>('all');
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [confidenceFilter, setConfidenceFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  /**
   * Whether to bring in items that cite the same passage as a match.
   *
   * Off by default, and it was the other way round. The rule it serves is
   * real — an item read without the one citing the same clause can mislead —
   * but applying it while a filter is active makes the filter a suggestion:
   * asking for high confidence returned 56 items of which 32 were not high
   * confidence, correctly labelled and completely unhelpful. Reported twice as
   * the filter being broken, which is the right way to describe it.
   *
   * The rule still holds where it matters. With no filter active every item is
   * on screen, so co-cited items are read together anyway. A filter is a
   * person saying which subset they want, and the companions are one click
   * away when they want the context back.
   */
  const [showRelated, setShowRelated] = useState(false);
  const docPaneRef = useRef<HTMLDivElement>(null);

  const documents = useQuery({
    queryKey: ['documents'],
    queryFn: () => api.get<{ documents: DocumentRow[] }>('/api/documents'),
  });

  const withItems = (documents.data?.documents ?? []).filter((d) => Number(d.item_count) > 0);
  const documentId = route.params.get('doc') ?? withItems[0]?.id ?? null;

  const items = useQuery({
    queryKey: ['items', documentId],
    queryFn: () => api.get<{ items: Item[]; groups: { key: string; itemIds: string[] }[] }>(
      `/api/documents/${documentId}/items`,
    ),
    enabled: Boolean(documentId),
  });

  const blocks = useQuery({
    queryKey: ['blocks', documentId],
    queryFn: () => api.get<{ blocks: Block[] }>(`/api/documents/${documentId}/blocks`),
    enabled: Boolean(documentId),
  });

  // Only needed for the run history, so it is fetched but never blocks the page.
  const detail = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => api.get<{
      runs: {
        id: string; status: string;
        unparsed_sections?: string[] | null;
        created_at?: string;
      }[];
    }>(`/api/documents/${documentId}`),
    enabled: Boolean(documentId),
  });
  const completeRuns = (detail.data?.runs ?? []).filter((r) => r.status === 'complete');

  /**
   * What the newest run could not read.
   *
   * The database has recorded this since the first extraction and nothing had
   * ever displayed it, so an item the structuring pass mangled — or, now, a
   * whole batch it could not complete — went missing from a contract with no
   * indication on the page that anything was absent. A reviewer counting
   * obligations against a specification had no way to know the list was short.
   */
  const unreadSections = (completeRuns[0]?.unparsed_sections ?? []).filter(Boolean);

  const reviewMany = useMutation({
    mutationFn: ({ ids, action }: { ids: string[]; action: string }) =>
      api.post<{ reviewed: number }>('/api/items/review-many', { ids, action }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items', documentId] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['quality'] });
    },
  });

  const review = useMutation({
    mutationFn: ({ id, action, title, detail }: { id: string; action: string; title?: string; detail?: string }) =>
      api.post(`/api/items/${id}/review`, { action, title, detail }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items', documentId] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['quality'] });
      queryClient.invalidateQueries({ queryKey: ['corrections'] });
      queryClient.invalidateQueries({ queryKey: ['item-history'] });
    },
  });

  const allItems = items.data?.items ?? [];

  /**
   * Items citing the same clause must be shown together. The demonstration's
   * rule was "two separate items, but they must appear together, not in
   * separate areas", and a filter that splits a group breaks exactly that —
   * so when any member of a group matches the filter, the whole group shows,
   * with the members that did not match marked.
   *
   * An item can now be in more than one group: one for each passage or clause
   * it shares with another item. It used to be exactly one, because the server
   * took a transitive closure and produced components rather than groups —
   * which on a real contract meant one component containing nearly everything,
   * and a filter that therefore filtered nothing.
   */
  const groupsOf = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const g of items.data?.groups ?? []) {
      for (const id of g.itemIds) {
        const keys = map.get(id) ?? new Set<string>();
        keys.add(g.key);
        map.set(id, keys);
      }
    }
    return map;
  }, [items.data]);

  /**
   * Every word of an item a search could reasonably be looking for, including
   * the passage it cites.
   *
   * Searching the extracted text alone would miss the case this exists for:
   * somebody knows a phrase is in the contract and wants to find what was
   * made of it. The cited text is the document's own words, so searching it
   * searches the source, for the parts the extraction actually reached.
   */
  const haystack = (item: Item) =>
    [
      item.title,
      item.detail,
      item.clause_reference ?? '',
      item.type,
      ...(item.citations ?? []).map((c) => c.citedText ?? ''),
    ].join(' ').toLowerCase();

  const needle = search.trim().toLowerCase();

  const matches = (item: Item) =>
    (typeFilter === 'all' || item.type === typeFilter) &&
    (statusFilter === 'all' || item.status === statusFilter) &&
    (confidenceFilter === 'all' || item.confidence === confidenceFilter) &&
    (needle === '' || haystack(item).includes(needle));

  /** The items the filters actually selected, before any companions. */
  const matching = useMemo(
    () => new Set(allItems.filter(matches).map((i) => i.id)),
    [allItems, typeFilter, statusFilter, confidenceFilter, needle],
  );

  /** Items that cite the same passage as something that matched. */
  const related = useMemo(() => {
    const keptGroups = new Set<string>();
    for (const id of matching) {
      for (const key of groupsOf.get(id) ?? []) keptGroups.add(key);
    }
    return new Set(
      allItems
        .filter(
          (i) =>
            !matching.has(i.id) &&
            [...(groupsOf.get(i.id) ?? [])].some((key) => keptGroups.has(key)),
        )
        .map((i) => i.id),
    );
  }, [allItems, matching, groupsOf]);

  const visible = useMemo(
    () =>
      allItems.filter((i) => matching.has(i.id) || (showRelated && related.has(i.id))),
    [allItems, matching, related, showRelated],
  );

  const filtersActive =
    typeFilter !== 'all' || statusFilter !== 'all' || confidenceFilter !== 'all' || needle !== '';

  /**
   * Of what is on screen, the ones a bulk accept would actually change.
   *
   * Restricted to items the filters selected, not to everything visible. A
   * companion is shown for context and was filtered out on purpose, and
   * sweeping it into an accept — under a confirmation that names the filter it
   * failed — is an approval nobody gave.
   */
  const pendingVisible = useMemo(
    () => visible.filter((i) => i.status === 'pending' && matching.has(i.id)),
    [visible, matching],
  );

  // Blocks highlighted for the selected item.
  const highlighted = useMemo(() => {
    const item = allItems.find((i) => i.id === selectedItem);
    return new Set((item?.citations ?? []).map((c) => c.blockIndex));
  }, [selectedItem, allItems]);

  /** Reverse map, so a cited passage can open the item that cites it. */
  const itemsByBlock = useMemo(() => {
    const map = new Map<number, string[]>();
    for (const item of allItems) {
      for (const c of item.citations ?? []) {
        const list = map.get(c.blockIndex) ?? [];
        list.push(item.id);
        map.set(c.blockIndex, list);
      }
    }
    return map;
  }, [allItems]);

  /** Blocks grouped into pages, matching the document's own structure. */
  const pages = useMemo(() => {
    const out: { pageNumber: number; blocks: Block[] }[] = [];
    for (const b of blocks.data?.blocks ?? []) {
      const last = out[out.length - 1];
      if (last && last.pageNumber === b.page_number) last.blocks.push(b);
      else out.push({ pageNumber: b.page_number, blocks: [b] });
    }
    return out;
  }, [blocks.data]);

  useEffect(() => {
    if (!selectedItem || !highlighted.size) return;
    const first = Math.min(...highlighted);
    docPaneRef.current
      ?.querySelector(`[data-block="${first}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedItem, highlighted]);

  if (documents.isLoading) return <Loading what="Loading contracts" />;

  if (!documentId) {
    return (
      <>
        <PageHeader title="Contract Breakdown" />
        <Empty>
          No contract has been extracted yet. Upload one on the Documents page and run an extraction.
        </Empty>
      </>
    );
  }

  const current = withItems.find((d) => d.id === documentId) ?? documents.data?.documents.find((d) => d.id === documentId);

  const counts = allItems.reduce<Record<string, number>>((acc, i) => {
    acc[i.type] = (acc[i.type] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <a className="back-link" href="#source">&larr; Documents</a>
      <PageHeader
        title={current?.title ?? 'Contract Breakdown'}
        description="Everything InBrief found in this contract, next to the passage it came from. Click an item to highlight its source."
      />

      {withItems.length > 1 ? (
        <div className="contract-picker">
          {withItems.map((d) => (
            <button
              key={d.id}
              className={`seg-tab${d.id === documentId ? ' active' : ''}`}
              onClick={() => { setSelectedItem(null); navigate('breakdown', { doc: d.id }); }}
            >
              {d.title}
            </button>
          ))}
        </div>
      ) : null}

      {canReview ? <ExtractionQuality /> : null}
      {canReview && documentId && completeRuns.length > 1 ? (
        <RunComparison documentId={documentId} runId={completeRuns[0]!.id} />
      ) : null}
      {canReview ? <Corrections /> : null}

      {/*
        The point of the extraction half: getting the reviewed set out, into
        the spreadsheet it is meant to replace. Reviewed items only by default
        — exporting unreviewed model output alongside checked items would
        launder one into the other.
      */}
      <div className="row-gap" style={{ marginBottom: 16 }}>
        <a className="btn btn-primary btn-small" href={`/api/documents/${documentId}/items/export`}>
          Export the accepted items
        </a>
        <a
          className="btn btn-ghost btn-small"
          href={`/api/documents/${documentId}/items/export?include=all`}
        >
          Export everything, including unreviewed
        </a>
      </div>

      <div className="filter-chips">
        <button className={`chip${typeFilter === 'all' ? ' active' : ''}`} onClick={() => setTypeFilter('all')}>
          All types <span className="chip-count">{allItems.length}</span>
        </button>
        {(Object.keys(ITEM_TYPE_LABELS) as ItemType[])
          .filter((t) => counts[t])
          .map((t) => (
            <button
              key={t}
              className={`chip${typeFilter === t ? ' active' : ''}`}
              onClick={() => setTypeFilter(t)}
            >
              {ITEM_TYPE_LABELS[t]} <span className="chip-count">{counts[t]}</span>
            </button>
          ))}
        <span className="push-right">
          <label className="visually-hidden" htmlFor="confidenceFilter">Filter by confidence</label>
          <select
            id="confidenceFilter"
            value={confidenceFilter}
            onChange={(e) => setConfidenceFilter(e.target.value)}
          >
            <option value="all">Any confidence</option>
            <option value="low">Low confidence</option>
            <option value="medium">Medium confidence</option>
            <option value="high">High confidence</option>
          </select>
          <label className="visually-hidden" htmlFor="statusFilter">Filter by review status</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Any status</option>
            <option value="pending">Awaiting review</option>
            <option value="accepted">Accepted</option>
            <option value="edited">Edited</option>
            <option value="rejected">Rejected</option>
          </select>
        </span>
      </div>

      <div className="breakdown-search">
        <label className="visually-hidden" htmlFor="itemSearch">
          Search the extracted items and the passages they cite
        </label>
        <input
          id="itemSearch"
          type="search"
          value={search}
          placeholder="Search items and cited text — a code, a figure, a phrase"
          onChange={(e) => setSearch(e.target.value)}
        />
        {needle ? (
          <p className="field-hint" role="status">
            {visible.length} of {allItems.length} shown.
            {' '}
            <button className="btn btn-ghost btn-small" onClick={() => setSearch('')}>Clear</button>
          </p>
        ) : null}
      </div>

      {/*
        What the filters did, in numbers.

        Without this the companions are invisible as a category: you ask for
        low confidence, get a list that is mostly not low confidence, and the
        only explanation is a note above individual cards. Saying how many were
        selected and how many came along for context makes the behaviour
        legible, and the toggle makes it optional.
      */}
      {filtersActive ? (
        <p className="filter-summary" role="status">
          {matching.size} of {allItems.length} items match these filters.
          {related.size > 0 ? (
            <>
              {' '}{related.size} other{related.size === 1 ? '' : 's'} cite the same passage as
              one that matched.
              {' '}
              <button
                type="button"
                className="btn btn-ghost btn-small"
                aria-pressed={showRelated}
                onClick={() => setShowRelated((v) => !v)}
              >
                {showRelated ? 'Hide related items' : 'Show related items'}
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      {/*
        Accepting what is on screen, in one action.
        A large contract yields a couple of hundred items and accepting them
        one at a time is most of an afternoon — but the acceptance is still a
        person's, made after looking at a filtered list, and every item gets
        its own review row with their name on it. Nothing accepts itself.
      */}
      {canReview && pendingVisible.length > 1 ? (
        <div className="bulk-review">
          <span>
            {pendingVisible.length} of the items matching these filters are awaiting review
            {confidenceFilter !== 'all' ? ` at ${confidenceFilter} confidence` : ''}.
          </span>
          <button
            className="btn btn-secondary btn-small"
            disabled={reviewMany.isPending}
            onClick={() => {
              if (!window.confirm(bulkAcceptWarning(pendingVisible.length, confidenceFilter))) return;
              reviewMany.mutate({ ids: pendingVisible.map((i) => i.id), action: 'accepted' });
            }}
          >
            {reviewMany.isPending ? 'Accepting…' : `Accept these ${pendingVisible.length}`}
          </button>
        </div>
      ) : null}

      {/*
        Not an error — the extraction completed. It is a statement that this
        list is short, which is the one thing a reviewer checking a contract
        against it has to be told.
      */}
      {unreadSections.length ? (
        <div className="note-box warn" role="status">
          <strong>
            Part of this document was not extracted
            {unreadSections.length > 1 ? ` (${unreadSections.length} notes)` : ''}.
          </strong>
          <ul className="mk-list unread-sections">
            {unreadSections.map((note) => <li key={note}>{note}</li>)}
          </ul>
          <p className="field-hint">
            Everything below is genuine and cited. It is not everything the document contains.
            Re-reading the document will try the missing part again.
          </p>
        </div>
      ) : null}

      <ErrorNote error={items.error ?? blocks.error ?? review.error} />

      <div className="breakdown-layout">
        <div className="extract-list">
          {items.isLoading ? <Loading what="Loading extracted items" /> : null}
          {!items.isLoading && visible.length === 0 ? (
            <Empty>Nothing matches those filters.</Empty>
          ) : null}

          {visible.map((item) => {
            const isGrouped = (groupsOf.get(item.id)?.size ?? 0) > 0;
            const notDirectlyMatching = isGrouped && !matching.has(item.id);

            return (
              <div key={item.id} className={isGrouped ? 'extract-group' : undefined}>
                {isGrouped && notDirectlyMatching ? (
                  <div className="group-note">
                    Shown because it cites the same clause as an item above — reading one without the
                    other would mislead.
                  </div>
                ) : null}

                {/*
                  The card is not itself a button. It contains Accept, Edit
                  and Reject, and a control nested inside another control is
                  invalid — it breaks screen reader navigation, because the
                  inner buttons become unreachable by the usual means.
                  The title is the control; the card keeps a click handler for
                  pointer convenience without claiming to be interactive.
                */}
                <article
                  className={`extract-item${selectedItem === item.id ? ' active' : ''}`}
                  onClick={() => setSelectedItem(item.id === selectedItem ? null : item.id)}
                >
                  <div className="ex-head">
                    <span className={`ex-tag ${TYPE_TAG[item.type]}`}>{ITEM_TYPE_LABELS[item.type]}</span>
                    {item.clause_reference ? <span className="ex-ref">{item.clause_reference}</span> : null}
                    <span className="push-right row-gap">
                      <Pill tone={CONFIDENCE_TONE[item.confidence]!}>{item.confidence} confidence</Pill>
                      <Pill tone={STATUS_TONE[item.status]!}>{item.status}</Pill>
                    </span>
                  </div>

                  <h3 className="ex-title">
                    <button
                      type="button"
                      className="ex-title-btn"
                      aria-pressed={selectedItem === item.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedItem(item.id === selectedItem ? null : item.id);
                      }}
                    >
                      {item.edited_title ?? item.title}
                    </button>
                  </h3>
                  <p className="ex-meta">{item.edited_detail ?? item.detail}</p>

                  <div className="ex-meta row-gap">
                    {item.applies_to ? <span>Applies to: {item.applies_to}</span> : null}
                    {item.due_date ? <span>Due: {item.due_date}</span> : null}
                    {item.frequency ? <span>Frequency: {item.frequency}</span> : null}
                    {item.value !== null ? <span>Value: {item.value}{item.unit ?? ''}</span> : null}
                  </div>

                  {item.citation_mismatch ? (
                    <div className="warn-box">
                      The quoted passage no longer matches the document exactly. Check the source
                      before accepting this item.
                    </div>
                  ) : null}

                  <div className="cites">
                    {item.citations.map((c) => (
                      <span className="cite" key={c.id}>page {c.pageNumber}</span>
                    ))}
                  </div>

                  {canReview ? (
                    <div className="review-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-primary btn-small"
                        disabled={review.isPending || item.status === 'accepted'}
                        onClick={() => review.mutate({ id: item.id, action: 'accepted' })}
                      >
                        Accept
                      </button>
                      <button
                        className="btn btn-secondary btn-small"
                        disabled={review.isPending}
                        onClick={() => {
                          const title = window.prompt('Corrected title', item.edited_title ?? item.title);
                          if (title === null) return;
                          const detail = window.prompt('Corrected detail', item.edited_detail ?? item.detail);
                          if (detail === null) return;
                          review.mutate({ id: item.id, action: 'edited', title, detail });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-ghost btn-small"
                        disabled={review.isPending || item.status === 'rejected'}
                        onClick={() => review.mutate({ id: item.id, action: 'rejected' })}
                      >
                        Reject
                      </button>
                      {item.status !== 'pending' ? (
                        <button
                          className="btn btn-ghost btn-small"
                          onClick={() => setHistoryFor(historyFor === item.id ? null : item.id)}
                        >
                          {historyFor === item.id ? 'Hide' : 'Show'} history
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {historyFor === item.id ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <ItemHistory itemId={item.id} />
                    </div>
                  ) : null}
                </article>
              </div>
            );
          })}
        </div>

        {/*
          Structure follows the demonstration exactly: .doc-col is the sticky
          wrapper, .doc-pane scrolls, and .doc-page is one page of the document
          — not one paragraph. The dashed rule between .doc-page siblings is a
          page break, so using it per paragraph turned continuous prose into a
          list of separated fragments.
        */}
        <div className="doc-col">
          <div className="doc-toolbar">
            <strong>Source document</strong>
            <a
              className="btn btn-ghost btn-small"
              href={`/api/documents/${documentId}/file`}
              target="_blank"
              rel="noopener"
              title="The PDF as uploaded — useful where the text layer has mangled a table"
            >
              Open the PDF
            </a>
            <span className="push-right doc-hint">
              {selectedItem
                ? `${highlighted.size} passage${highlighted.size === 1 ? '' : 's'} highlighted`
                : 'Select an item to highlight its source'}
            </span>
          </div>
          <div className="doc-pane" ref={docPaneRef}>
            {blocks.isLoading ? <Loading what="Loading the document" /> : null}
            {pages.map((page) => (
              <div className="doc-page" key={page.pageNumber}>
                <div className="doc-page-num">Page {page.pageNumber}</div>
                {page.blocks.map((b) => {
                  const citedBy = itemsByBlock.get(b.block_index) ?? [];
                  const lit = highlighted.has(b.block_index);
                  const citable = citedBy.length > 0;
                  return (
                    <p
                      key={b.id}
                      data-block={b.block_index}
                      className={citable ? `doc-hl${lit ? ' is-lit' : ''}` : undefined}
                      // Clicking a cited passage selects the item that cites
                      // it, so the link works in both directions.
                      onClick={citable ? () => setSelectedItem(citedBy[0]!) : undefined}
                      role={citable ? 'button' : undefined}
                      tabIndex={citable ? 0 : undefined}
                      title={citable ? 'Cited by an extracted item — click to open it' : undefined}
                      onKeyDown={citable ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedItem(citedBy[0]!);
                        }
                      } : undefined}
                    >
                      {b.clause_ref ? <strong>{b.clause_ref} </strong> : null}
                      {b.text}
                    </p>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * What a bulk acceptance is, said before it happens.
 *
 * The person pressing this is making the same claim they make on every item
 * individually — that they have looked and it is right. Saying so is the
 * difference between a shortcut and a rubber stamp, and the sentence is short
 * enough to read.
 */
function bulkAcceptWarning(count: number, confidence: string): string {
  const scope = confidence === 'all' ? '' : ` at ${confidence} confidence`;
  return (
    `Accept ${count} item(s)${scope}?\n\n` +
    'This records you as having reviewed each of them, exactly as if you had ' +
    'accepted them one by one — your name goes on every one.\n\n' +
    'Accepted items are what the exports and the breakdown treat as agreed, ' +
    'so only do this for items you have actually looked at.'
  );
}
